/* ============================================================================
   useVehiclesVessels — THE WIRING SEAM
   ----------------------------------------------------------------------------
   This is the ONLY file to edit when wiring Vehicles & Vessels into the live
   system. VehiclesVesselsPage.tsx and VehiclesVesselsPage.css are the locked
   visual and must not change during wiring. Everything the page needs — the
   list and the mutations — comes from this hook. Today it holds mock data in
   memory; to go live, replace each marked SEAM with the real operator-scoped
   API call against the existing `vessels` table.

   Field mapping (view-model -> database `aquorix.vessels`):
     id     -> vessel_id
     name   -> name
     type   -> asset_type        ('trolley' | 'vehicle' | 'vessel')  [CHECK-constrained]
     cap    -> max_capacity      (integer, nullable — one number, labeled by type in the UI)
     desc   -> vessel_type        (free-text descriptor: "Day boat", "Hiace", "RIB")
     notes  -> notes
     active -> is_active         (soft-delete: deactivate, never destroy)

   Tenant rule (non-negotiable): every read and write is scoped to the
   authenticated operator server-side (operator_id from requireDashboardScope).
   No operator_id is ever accepted from the client. Assets are purely
   operator-owned; there is no shared catalog, so the read is a plain
   operator filter — no cross-operator leak surface here.

   Soft-delete only: removing an asset from service sets is_active=false. Never
   hard-delete a vessels row — dive_sessions.vessel_id and
   operator_schedule_patterns.vessel_id reference it (ON DELETE SET NULL), and a
   destroy would silently strip transport from historical sessions and rhythms.
   ============================================================================ */
import { useState, useCallback, useEffect } from "react";
import { apiFetch } from "../../../utils/api";

export type AssetType = "vehicle" | "vessel" | "trolley";

export type Asset = {
  id: number;
  name: string;
  type: AssetType;
  cap: number;
  desc: string;
  notes: string;
  active: boolean;
};

export type NewAsset = {
  name: string;
  type: AssetType;
  cap: number;
  desc: string;
  notes: string;
  active: boolean;
};

export type VehiclesVesselsApi = {
  assets: Asset[];
  loading: boolean;
  /** Create an operator-owned asset (is_active defaults to the form value). */
  createAsset: (a: NewAsset) => void;
  /** Update an owned asset's fields. */
  updateAsset: (id: number, patch: Partial<NewAsset>) => void;
  /** Soft delete / restore: flip is_active. Never destroys the row. */
  setActive: (id: number, active: boolean) => void;
};

/**
 * Data and mutations for the Vehicles & Vessels page.
 * The server derives operator scope from requireDashboardScope.
 */
export function useVehiclesVessels(): VehiclesVesselsApi {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAssets = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiFetch(
        "/api/v1/dashboard/vehicles-vessels",
        { method: "GET" }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        assets?: Asset[];
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      setAssets(
        Array.isArray(payload.assets) ? payload.assets : []
      );
    } catch (error) {
      console.error(
        "[Vehicles & Vessels] Load failed:",
        error
      );
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAssets();
  }, [loadAssets]);

  const createAsset = useCallback(async (asset: NewAsset) => {
    try {
      const response = await apiFetch(
        "/api/v1/dashboard/vehicles-vessels",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: asset.name,
            asset_type: asset.type,
            max_capacity: asset.cap,
            vessel_type: asset.desc || null,
            notes: asset.notes || null,
            is_active: asset.active,
          }),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadAssets();
    } catch (error) {
      console.error(
        "[Vehicles & Vessels] Create failed:",
        error
      );
    }
  }, [loadAssets]);

  const updateAsset = useCallback(async (
    id: number,
    patch: Partial<NewAsset>
  ) => {
    const body: Record<string, unknown> = {};

    if (patch.name !== undefined) {
      body.name = patch.name;
    }
    if (patch.type !== undefined) {
      body.asset_type = patch.type;
    }
    if (patch.cap !== undefined) {
      body.max_capacity = patch.cap;
    }
    if (patch.desc !== undefined) {
      body.vessel_type = patch.desc || null;
    }
    if (patch.notes !== undefined) {
      body.notes = patch.notes || null;
    }
    if (patch.active !== undefined) {
      body.is_active = patch.active;
    }

    try {
      const response = await apiFetch(
        `/api/v1/dashboard/vehicles-vessels/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadAssets();
    } catch (error) {
      console.error(
        "[Vehicles & Vessels] Update failed:",
        error
      );
    }
  }, [loadAssets]);

  const setActive = useCallback(async (
    id: number,
    active: boolean
  ) => {
    try {
      const response = await apiFetch(
        `/api/v1/dashboard/vehicles-vessels/${id}/active`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_active: active }),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadAssets();
    } catch (error) {
      console.error(
        "[Vehicles & Vessels] Status update failed:",
        error
      );
    }
  }, [loadAssets]);

  return {
    assets,
    loading,
    createAsset,
    updateAsset,
    setActive,
  };
}
